import React from 'react';
import { useTheme } from '../theme';
import { useLanguage } from '../App';

// PhoneSection.js
// A compact, rounded phone box with green stroke and site-gray background.
// Props:
//   - phone: string (e.g. "+40712345678") — used for tel: link and copy
//   - example: string (display fallback when phone is not provided)
//   - label: string (small caption above the number)

export default function PhoneSection({ phone, example = '+40 712 345 678' }) {
  const { tokens } = useTheme();
  const { language } = useLanguage();
  const display = phone && phone.length ? phone : example;

  // Normalize tel: link (strip spaces and non-digit plus signs)
  const telLink = phone
    ? `tel:${phone.replace(/[^+\d]/g, '')}`
    : `tel:${example.replace(/[^+\d]/g, '')}`;

  const isMobile = () => {
    if (typeof window === 'undefined') return false;
    const coarse = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
    const ua = (navigator && navigator.userAgent) || '';
    const byUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
    return coarse || byUA;
  };

  const handleClick = (e) => {
    e.preventDefault();
    if (isMobile()) {
      // On phones: open dialer
      window.location.href = telLink;
    } else {
      // On desktop: copy number
      navigator.clipboard
        .writeText(display)
        .then(() => {
          try { window.navigator.vibrate && window.navigator.vibrate(30); } catch (err) {}
          alert(language === 'ro' ? 'Număr copiat: ' + display : 'Number copied: ' + display);
        })
        .catch(() => alert(language === 'ro' ? 'Nu am putut copia numărul.' : 'Could not copy number.'));
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      handleClick(e);
    }
  };

  const small = typeof window !== 'undefined' ? window.innerWidth < 480 : false;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      aria-label="Phone contact"
      style={{
        background: tokens.surface,
        border: `2px solid ${tokens.accent}`,
        borderRadius: 14,
  padding: small ? '6px 16px' : '8px 25px',
        display: 'flex',
        alignItems: 'center',
  gap: small ? 8 : 10,
        width: 'fit-content',
        boxShadow: `0 6px 18px ${tokens.shadow}`,
        cursor: 'pointer',
      }}
    >
      {/* Phone illustration */}
      <div
        style={{
          minWidth: small ? 36 : 44,
          minHeight: small ? 36 : 44,
          borderRadius: small ? 8 : 10,
          background: 'linear-gradient(180deg, rgba(255,255,255,0.03), rgba(0,0,0,0.04))',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.02)',
        }}
      >
        {/* simple phone SVG */}
        <svg width={small ? 18 : 22} height={small ? 18 : 22} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
          <rect x="6" y="2" width="12" height="20" rx="3" stroke={tokens.accent} strokeWidth="1.4" fill="transparent" />
          <circle cx="12" cy="18" r="0.9" fill={tokens.accent} />
          <path d="M8.5 7.5h7" stroke={tokens.accent} strokeWidth="1.2" strokeLinecap="round" />
        </svg>
      </div>

      {/* Text area */}
      <div style={{ flex: 1, minWidth: 0 }}>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span
            style={{
              color: tokens.text,
              fontSize: 18,
              fontWeight: 800,
              letterSpacing: '0.01em',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              userSelect: 'text',
            }}
          >
            {display}
          </span>

        </div>

        {/* no label/tagline */}
      </div>
    </div>
  );
}
